
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReportTable from "../../ReportTable";
import CodingKpisFilter from "../CodingKpisFilter/CodingKpisFilter";
import { useReport } from "../../hooks/useReport";
import { batchUpdateAppliedFilters, resetCoidngKPISFilters } from "../../redux/ReportsSlice";
import { useSearchParams } from "react-router-dom";

const REPORT_PATH = "/coding-kpis-report";
const SESSION_KEY = "codingKpis_localFilters";

// Reusable helper — converts sessionStorage shape → API payload shape
const buildApiPayload = (parsed) => ({
  user_id: parsed.coder || [],
  site: parsed.site || [],
  episodes_coded: parsed.episodes_coded || [],
  codinging_days: [],
  coding_days_operator: "",
  total_episodes_coded: [],
  total_episodes_coded_operator: "",
  manually_coded: [],
  manually_coded_operator: "",
  ai_validation_coded: [],
  ai_validation_coded_operator: "",
  nwau: [],
  average_los: [],
  average_los_operator: "",
  start_date: parsed.start_date || null,
  end_date: parsed.end_date || null,
  // sort_by: parsed.sort_by || "",
  // sort_order: parsed.sort_order || "",
});

const CodingKpisOverview = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const appliedFilters = useSelector(
    (s) => s.reports.appliedFilters[REPORT_PATH] || {}
  );

  const showReport = searchParams.get("view") === "table";
  const hasValidDates = !!(appliedFilters?.start_date && appliedFilters?.end_date);

  // On mount — restore from sessionStorage if Redux is empty (page reload case)
  useEffect(() => {
    if (!hasValidDates) {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed?.start_date && parsed?.end_date) {
            dispatch(
              batchUpdateAppliedFilters({
                reportPath: REPORT_PATH,
                filters: buildApiPayload(parsed),
              })
            );
          } else {
            if (showReport) setSearchParams({});
          }
        } catch {
          if (showReport) setSearchParams({});
        }
      } else {
        if (showReport) setSearchParams({});
      }
    }
  }, []); // only on mount

  const {
    episodes,
    totalPages,
    pageNumber,
    setPageNumber,
    searchText,
    setSearchText,
    handleSearchChange,
    reportLoading,
    reportError,
    exportLoading,
    resetSignal,
    setSortPayload,
  } = useReport(REPORT_PATH, { enabled: showReport && hasValidDates });

  const handleApplyFilters = useCallback((filtersPayload) => {
    dispatch(
      batchUpdateAppliedFilters({
        reportPath: REPORT_PATH,
        filters: filtersPayload,
      })
    );
    setPageNumber(1);
    setTimeout(() => {
      setSearchParams({ view: "table" });
    }, 0);
  }, [dispatch, setPageNumber, setSearchParams]);
const handleColumnFilterApply = useCallback(
  (filters) => {
    let sessionDates = { start_date: null, end_date: null };
    let sessionUserIds = [];

    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        sessionDates = {
          start_date: parsed.start_date || null,
          end_date:   parsed.end_date   || null,
        };
        sessionUserIds = parsed.coder || [];
      }
    } catch { /* ignore */ }

    const incomingUserIds = filters.user_id || filters.coder || [];
    const isClearing = incomingUserIds.length === 0;

    const merged = {
      ...appliedFilters,
      ...filters,
      //  ALWAYS preserve dates — never let column filter wipe them
      start_date: filters.start_date || sessionDates.start_date || appliedFilters.start_date,
      end_date:   filters.end_date   || sessionDates.end_date   || appliedFilters.end_date,
      //  clearing → restore original session users
      // applying → use newly selected user from column filter
      user_id: isClearing ? sessionUserIds : incomingUserIds,
    };

    dispatch(batchUpdateAppliedFilters({ reportPath: REPORT_PATH, filters: merged }));
    setPageNumber(1);
  },
  [dispatch, setPageNumber, appliedFilters]
);
  // const handleColumnFilterApply = useCallback(
  //   (filters) => {
  //     // Read original dates from sessionStorage (set when user clicked Generate Report)
  //     // These are the "base" dates from the filter page — never lose them
  //     let sessionDates = { start_date: null, end_date: null };
  //     try {
  //       const saved = sessionStorage.getItem(SESSION_KEY);
  //       if (saved) {
  //         const parsed = JSON.parse(saved);
  //         sessionDates = {
  //           start_date: parsed.start_date || null,
  //           end_date:   parsed.end_date   || null,
  //         };
  //       }
  //     } catch { /* ignore */ }

  //     const merged = {
  //       ...appliedFilters,       // current Redux state as base
  //       ...filters,              // column filter overrides
  //       // If column filter cleared the dates (null/undefined),
  //       // fall back to original session dates from filter page
  //       start_date: filters.start_date || sessionDates.start_date,
  //       end_date:   filters.end_date   || sessionDates.end_date,
  //     };

  //     dispatch(
  //       batchUpdateAppliedFilters({
  //         reportPath: REPORT_PATH,
  //         filters: merged,
  //       })
  //     );
  //     setPageNumber(1);
  //   },
  //   [dispatch, setPageNumber, appliedFilters]
  // );

  // FIX 2: custom reset that restores dates from session instead of wiping Redux
  const handleReset = useCallback(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        dispatch(
          batchUpdateAppliedFilters({
            reportPath: REPORT_PATH,
            filters: buildApiPayload(parsed), // re-apply original dates — table stays visible
          })
        );
      } catch { /* ignore */ }
    }
    setPageNumber(1);
    setSearchText("");
    setSortPayload({});
  }, [dispatch, setPageNumber, setSearchText, setSortPayload]);

  const handleBack = () => {
    setSearchParams({});
    dispatch(resetCoidngKPISFilters());
    sessionStorage.removeItem(SESSION_KEY);
  };

  // If URL says table but no valid dates yet (rehydration in progress on reload)
  // → show filter page instead of blank screen.
  // The useEffect above will either restore dates (API fires) or setSearchParams({})
  // (redirects URL to filter page). Either way user sees something, never blank.
  if (showReport && !hasValidDates) {
    return (
      <div className="hac-report-table-container mt-3 px-2">
        <CodingKpisFilter onApply={handleApplyFilters} isFilterPage={false}/>
      </div>
    );
  }

  return (
    <div className="hac-report-table-container mt-3 px-2">
      {!showReport ? (
        <CodingKpisFilter onApply={handleApplyFilters} isFilterPage={true}/>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
            <h4 className="pb-2">Coding KPI Stats Report</h4>
            <button className="kpi-reset" onClick={handleBack}>Back</button>
          </div>
          <ReportTable
            episodes={episodes}
            isReportLoading={reportLoading}
            isReportError={reportError}
            reportPageNumber={pageNumber}
            setReportPageNumber={setPageNumber}
            reportExcelLoading={exportLoading}
            handleSearchChange={handleSearchChange}
            totalPages={totalPages}
            searchText={searchText}
            setSearchText={setSearchText}
            onSortChange={setSortPayload}
            // onReset={handleReset}
            resetSignal={resetSignal}
            onColumnFilterApply={handleColumnFilterApply}
          />
        </>
      )}
    </div>
  );
};

export default CodingKpisOverview;
